import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Divider, Typography, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import DonutChart from "./DonutChart";
import { getSOHDistribution, getSOHScatterData } from "../../../Api/BatteryHealthApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { useSOHBuckets, resolveSOHCategory } from "./utils/sohBuckets";
import { getBucketDisplayLabel, isUnavailableBucket } from "./utils/unavailableBucket";
import SOHScatterChart from "../../../Charts/Scatter/SOHScatterChart";
import { smartTooltipPos } from "./utils/chartUtils";
import { getLegendSwatchSx } from "../../../Charts/Scatter/scatterBucketUtils";
import CellChemistryQuickFilter from "../Components/CellChemistryQuickFilter";
import { buildCellChemistryFilters } from "./utils/cellChemistryFilter";

const X_FILTERS_BASE = [
  { key: "age",       label: "Age"        },
  { key: "bmsCycles", label: "BMS Cycles" },
];
const X_FILTER_ODOMETER = { key: "odometer", label: "Odometer" };

const AXIS_X_TO_FILTER = {
  addedOn:    { key: "age",       label: "Age"        },
  cycleCount: { key: "bmsCycles", label: "BMS Cycles" },
  odometer:   { key: "odometer",  label: "Odometer"   },
  latestSOHPredictionOdometer: { key: "latestSOHPredictionOdometer", label: "Odometer" },
};

const AXIS_X_TO_SELECT = {
  addedOn:    "calendarAgeDaysEst",
  cycleCount: "eqCycleEst",
  odometer:   "odometer",
  latestSOHPredictionOdometer: "latestSOHPredictionOdometer",
};

// Tooltip fields that map to a backend select metric (soh -> sohEst and
// odometer are already covered by the base select / isVehicle handling).
const TOOLTIP_KEY_TO_SELECT = {
  sohConfidence: "sohConfidence",
  latestSOHPredictionOdometer: "latestSOHPredictionOdometer",
  sohComputedDate: "sohComputedDate",
};

const SCATTER_CHART_ID = "soh-rul-scatter-chart";

// `buckets` resolves the unavailable bucket's org display label.
function formatDonutCenterLabel(categoryName, buckets) {
  if (!categoryName) return "All Batteries";
  if (isUnavailableBucket(categoryName)) return getBucketDisplayLabel(categoryName, buckets);
  if (categoryName.startsWith(">")) return `SOH > ${categoryName.slice(1)}%`;
  if (categoryName.startsWith("<")) return `SOH < ${categoryName.slice(1)}%`;
  if (categoryName.includes("-")) {
    const [low, high] = categoryName.split("-");
    if (low && high) return `SOH ${low}% to ${high}%`;
  }
  return `SOH ${categoryName}`;
}

// Compact form for the first entry of a multi-select ("SOH >98%" + "+ 2 more").
function formatDonutShortLabel(categoryName, buckets) {
  if (isUnavailableBucket(categoryName)) return getBucketDisplayLabel(categoryName, buckets);
  if (categoryName.startsWith(">") || categoryName.startsWith("<")) return `SOH ${categoryName}%`;
  return `SOH ${categoryName}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
const StateOfHealthChart = ({
  globalFilters = {},
  onDetailsClick,
  chartConfig = {},
  supportedDimensions = {},
  cellChemistryOptions = [],
  cellChemistry = null,
  onCellChemistryChange,
}) => {
  const { baseEntityType, baseEntityTypePluralLable } = useBaseEntityInfo();
  // Applied to both the donut and the scatter; [] when All is selected.
  const chemistryFilters = useMemo(() => buildCellChemistryFilters(cellChemistry), [cellChemistry]);
  const isVehicle   = baseEntityType === "vehicle";
  const sohBuckets  = useSOHBuckets();
  const donutColors = useMemo(() => sohBuckets.map((b) => b.color), [sohBuckets]);
  const donutNames  = useMemo(() => sohBuckets.map((b) => b.name),  [sohBuckets]);

  const scatterCombinations = chartConfig?.sohScatterChart?.axisCombinations || [];
  const scatterExtraSelect = useMemo(() => {
    const fromAxes     = scatterCombinations.map((c) => AXIS_X_TO_SELECT[c.x]);
    const fromTooltips = (chartConfig?.sohScatterChart?.tooltipFields || []).map((f) => TOOLTIP_KEY_TO_SELECT[f?.key]);
    return [...new Set([...fromAxes, ...fromTooltips].filter(Boolean))];
  }, [scatterCombinations, chartConfig]);

  const xFilters = scatterCombinations.length > 0
    ? scatterCombinations
        .filter((combo) => supportedDimensions[combo.x] !== false)
        .map((combo) => {
          const base = AXIS_X_TO_FILTER[combo.x];
          if (!base) return null;
          // org-meta title (if set) overrides both the filter pill and the axis title
          return combo.title ? { ...base, label: combo.title, axisLabel: combo.title } : base;
        })
        .filter(Boolean)
        .filter((f, i, arr) => arr.findIndex((a) => a.key === f.key) === i)
    : isVehicle
      ? [...X_FILTERS_BASE, X_FILTER_ODOMETER]
      : X_FILTERS_BASE;

  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [activeXFilter, setActiveXFilter] = useState(() => {
    const combos = chartConfig?.sohScatterChart?.axisCombinations || [];
    const defaultCombo = combos.find((c) => c.isDefault) ?? combos[0];
    return AXIS_X_TO_FILTER[defaultCombo?.x]?.key ?? "bmsCycles";
  });
  const [xFilterChanging,    setXFilterChanging]    = useState(false);
  const [donutHover,         setDonutHover]         = useState(null);

  // ─── Donut API state ───────────────────────────────────────────────────────
  const [donutLoading, setDonutLoading] = useState(true);
  const [donutError,   setDonutError]   = useState(false);
  const [donutCounts,  setDonutCounts]  = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchDonut = async () => {
      setDonutLoading(true);
      setDonutError(false);
      try {
        const res = await getSOHDistribution(globalFilters, chemistryFilters);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items  = res?.response?.data ?? [];
          const counts = Object.fromEntries(sohBuckets.map((b) => [b.name, 0]));
          items.forEach(({ xLabel, metricsArray }) => {
            if (xLabel in counts) {
              const metric = metricsArray?.find((m) => m.type === "DistinctVehicleCount");
              counts[xLabel] = metric?.value ?? 0;
            }
          });
          setDonutCounts(counts);
        } else {
          if (!cancelled) setDonutError(true);
        }
      } catch (_) {
        if (!cancelled) setDonutError(true);
      } finally {
        if (!cancelled) setDonutLoading(false);
      }
    };
    fetchDonut();
    return () => { cancelled = true; };
  }, [globalFilters, sohBuckets, chemistryFilters]);

  const totalBatteries = useMemo(
    () => Object.values(donutCounts).reduce((s, v) => s + v, 0),
    [donutCounts]
  );

  // ─── Scatter API state ─────────────────────────────────────────────────────
  const [scatterLoading,   setScatterLoading]   = useState(true);
  const [scatterError,     setScatterError]     = useState(false);
  const [scatterBatteries, setScatterBatteries] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchScatter = async () => {
      setScatterLoading(true);
      setScatterError(false);
      try {
        const res = await getSOHScatterData(globalFilters, isVehicle, chemistryFilters, scatterExtraSelect);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items     = res?.response?.data ?? [];
          const batteries = items.map(({ xLabel, metricsArray, metaInfo }) => {
            const getVal = (type) => metricsArray?.find((m) => m.type === type)?.value ?? null;
            const ageDays = getVal("calendarAgeDaysEst");
            return {
              batteryID:      xLabel,
              deviceID:       metaInfo?.deviceID,
              licensePlateNo: metaInfo?.licensePlateNo,
              soh:            getVal("sohEst"),
              age:            ageDays != null ? parseFloat((ageDays / 30.44).toFixed(1)) : null,
              ageDays,
              bmsCycles:      getVal("eqCycleEst"),
              odometer:       getVal("odometer"),
              latestSOHPredictionOdometer: getVal("latestSOHPredictionOdometer"),
              sohConfidence:  getVal("sohConfidence"),
              sohComputedDate: getVal("sohComputedDate"),
              // Raw metaInfo: the category is resolved in a memo below so org meta
              // arriving later does not re-run this request.
              sohMetaInfo:    metaInfo ?? null,
            };
          });
          setScatterBatteries(batteries);
        } else {
          if (!cancelled) setScatterError(true);
        }
      } catch (_) {
        if (!cancelled) setScatterError(true);
      } finally {
        if (!cancelled) setScatterLoading(false);
      }
    };
    fetchScatter();
    return () => { cancelled = true; };
  }, [globalFilters, isVehicle, scatterExtraSelect, chemistryFilters]);

  // Backend label wins ("SOH Unavailable" for null / negative SOH); see resolveSOHCategory.
  const categorisedScatterBatteries = useMemo(
    () => scatterBatteries.map((b) => ({
      ...b,
      category: resolveSOHCategory(b.sohMetaInfo, b.soh, sohBuckets),
    })),
    [scatterBatteries, sohBuckets]
  );

  // ─── X-filter change with transient loader ────────────────────────────────
  const handleXFilterChange = useCallback((key) => {
    setXFilterChanging(true);
    setActiveXFilter(key);
  }, []);

  useEffect(() => {
    if (!xFilterChanging) return;
    const t = setTimeout(() => setXFilterChanging(false), 50);
    return () => clearTimeout(t);
  }, [xFilterChanging]);

  // ─── Category selection ───────────────────────────────────────────────────
  const selectCategory = useCallback((name) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setDonutHover(null);
  }, []);

  // Reset also clears the chemistry back to All.
  const hasActiveSelection = selectedCategories.size > 0 || cellChemistry != null;
  const resetSelection = useCallback(() => {
    setSelectedCategories(new Set());
    setDonutHover(null);
    if (cellChemistry != null) onCellChemistryChange?.(null);
  }, [cellChemistry, onCellChemistryChange]);

  const donutCenterCount = useMemo(() => {
    if (selectedCategories.size === 0) return totalBatteries;
    return [...selectedCategories].reduce((sum, name) => sum + (donutCounts[name] ?? 0), 0);
  }, [selectedCategories, donutCounts, totalBatteries]);

  const donutCenterLabel = useMemo(() => {
    const allSelected = selectedCategories.size === sohBuckets.length;
    if (selectedCategories.size === 0 || allSelected)
      return { primary: `All ${baseEntityTypePluralLable}`, secondary: null };
    if (selectedCategories.size === 1)
      return { primary: formatDonutCenterLabel([...selectedCategories][0], sohBuckets), secondary: null };
    const [first, ...rest] = [...selectedCategories];
    return { primary: formatDonutShortLabel(first, sohBuckets), secondary: `+ ${rest.length} more` };
  }, [selectedCategories, sohBuckets, baseEntityTypePluralLable]);

  // ─── Scatter — visible groups ──────────────────────────────────────────────
  const batteriesByVisibleCategory = useMemo(
    () =>
      sohBuckets
        .filter((cat) => selectedCategories.size === 0 || selectedCategories.has(cat.name))
        .map((cat) => ({
          category:  cat,
          batteries: categorisedScatterBatteries.filter((b) => b.category === cat.name),
        })),
    [selectedCategories, categorisedScatterBatteries, sohBuckets]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E6E6E6", overflow: "clip" }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          px: "28px", pt: "24px", pb: "8px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#2E2E2E", lineHeight: "24px" }}>
            Digital Twin based SOH
          </Typography>
          <Box
            onClick={onDetailsClick}
            sx={{
              display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer",
              "&:hover": {
                "& .MuiTypography-root": { color: "#4D44D8" },
                "& .MuiSvgIcon-root":    { color: "#4D44D8" },
              },
            }}
          >
            <Typography variant="fw500s12lh20c000000" sx={{ color: "#6D72F6" }}>Details</Typography>
            <ChevronRightIcon sx={{ color: "#6D72F6", fontSize: "18px" }} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <CellChemistryQuickFilter
            value={cellChemistry}
            options={cellChemistryOptions}
            onChange={onCellChemistryChange}
            testIdPrefix="soh-overview"
          />
          <Button
            variant="outlined"
            size="small"
            onClick={resetSelection}
            disabled={!hasActiveSelection}
            sx={{
              textTransform: "none", fontSize: "12px", fontWeight: 500,
              minWidth: "auto", px: 1.5, py: "3px", borderRadius: "6px",
              color:       hasActiveSelection ? "#6D72F6" : "#9E9E9E",
              borderColor: hasActiveSelection ? "#6D72F6" : "#D1D5DB",
              "&:hover":      { borderColor: "#6D72F6", bgcolor: "#F5F5FF" },
              "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#9E9E9E" },
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* ── Legend row ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "28px", pt: "12px", pb: "12px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", flex: 1, justifyContent: "center" }}>
          {sohBuckets.map((cat) => {
            const isSelected  = selectedCategories.has(cat.name);
            const hasSelection = selectedCategories.size > 0;
            const isFaded     = hasSelection && !isSelected;
            return (
              <Box
                key={cat.name}
                data-testid={`soh-overview-legend-${cat.name}`}
                onClick={() => selectCategory(cat.name)}
                sx={{
                  display: "flex", alignItems: "center", gap: "5px",
                  cursor: "pointer", opacity: isFaded ? 0.35 : 1,
                  transition: "opacity 0.18s", userSelect: "none",
                  "&:hover": { opacity: isFaded ? 0.55 : 0.78 },
                }}
              >
                <Box sx={getLegendSwatchSx(cat, isFaded)} />
                <Typography sx={{ fontSize: "12px", color: "#444B51", whiteSpace: "nowrap", fontWeight: isSelected ? 600 : 400 }}>
                  {cat.displayLabel ?? cat.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Charts row ───────────────────────────────────────────────────── */}
      <Box sx={{ overflowX: "auto", pb: "24px", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", px: "28px", gap: "16px", minWidth: "900px" }}>

          {/* ── Donut ────────────────────────────────────────────────────── */}
          <Box sx={{ width: "270px", flexShrink: 0, position: "relative" }}>
            {donutLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "270px" }}>
                <CartLoader />
              </Box>
            ) : donutError ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "270px" }}>
                <Typography sx={{ fontSize: "13px", color: "#9E9E9E" }}>Failed to load</Typography>
              </Box>
            ) : (
              <>
                <DonutChart
                  series={sohBuckets.map((b) => donutCounts[b.name] ?? 0)}
                  colors={donutColors}
                  categoryNames={donutNames}
                  selectedCategories={selectedCategories}
                  onSelect={selectCategory}
                  onSliceMouseEnter={(e, i) => {
                    setDonutHover({ clientX: e.clientX, clientY: e.clientY, seriesIndex: i });
                  }}
                  onSliceMouseLeave={() => setDonutHover(null)}
                  size={270}
                  gapDeg={0}
                  sliceStrokeWidth={0}
                />
                <Box
                  sx={{
                    position: "absolute", top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center", pointerEvents: "none",
                    width: "130px",
                  }}
                >
                  <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#2E2E2E", lineHeight: 1 }}>
                    {donutCenterCount}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "#9E9E9E", mt: "4px", lineHeight: 1.4 }}>
                    {donutCenterLabel.primary}
                  </Typography>
                  {donutCenterLabel.secondary && (
                    <Typography sx={{ fontSize: "11px", color: "#9E9E9E", lineHeight: 1.4 }}>
                      {donutCenterLabel.secondary}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* ── Vertical separator ───────────────────────────────────────── */}
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mr: "10px", ml: "15px" }} />

          {/* ── Scatter ──────────────────────────────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: "580px", display: "flex", flexDirection: "column" }}>
            {scatterLoading || xFilterChanging ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "350px" }}>
                <CartLoader />
              </Box>
            ) : scatterError ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "350px" }}>
                <Typography sx={{ fontSize: "13px", color: "#9E9E9E" }}>Failed to load</Typography>
              </Box>
            ) : (
              <SOHScatterChart
                batteriesByVisibleCategory={batteriesByVisibleCategory}
                selectedCategories={selectedCategories}
                onCategorySelect={selectCategory}
                activeXFilter={activeXFilter}
                onXFilterChange={handleXFilterChange}
                xFilters={xFilters}
                chartId={SCATTER_CHART_ID}
                height={350}
                showLegend={false}
                isVehicle={isVehicle}
                tooltipFields={chartConfig?.sohScatterChart?.tooltipFields ?? null}
                tooltipIdentifier={chartConfig?.sohScatterChart?.tooltipIdentifier ?? null}
              />
            )}
          </Box>

        </Box>
      </Box>

      {/* ── Donut hover tooltip ───────────────────────────────────────────── */}
      {donutHover && (() => {
        const cat   = sohBuckets[donutHover.seriesIndex];
        const count = donutCounts[cat?.name] ?? 0;
        if (!cat || !count) return null;
        const pct = totalBatteries > 0 ? ((count / totalBatteries) * 100).toFixed(2) : "0.00";
        const { left, top } = smartTooltipPos(donutHover.clientX, donutHover.clientY, 210, 72);
        return (
          <Box sx={{
            position: "fixed", left, top, zIndex: 9999,
            bgcolor: "#fff", border: "1px solid #E6E6E6", borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", p: "12px 16px",
            minWidth: "210px", pointerEvents: "none",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cat.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: "#444B51" }}>
                Percentage of Fleet:&nbsp;
                <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                  {pct}%
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ width: 8, height: 8, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: "#444B51" }}>
                {`Number of ${baseEntityTypePluralLable}:`}&nbsp;
                <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                  {count}
                </Typography>
              </Typography>
            </Box>
          </Box>
        );
      })()}

    </Box>
  );
};

export default StateOfHealthChart;
